import React, { useContext, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';

import { AccountDataContext } from 'modules/context';
import { ButtonColour } from 'components/Button/constants';
import { contentIsNew } from 'modules/utils/newContentUtils';
import { ResourceHubItemType } from '../types';
import {
  getDateFormatted,
  getCMSLink,
  linkText,
  readTimeText,
} from 'modules/utils/utils';
import { ReactComponent as Chevron } from 'modules/theme/icons/chevrons/chevron-right.svg';
import * as Hyperlink from 'components/Hyperlink';
import CtaButton from 'components/Cta/Button';
import Social from 'components/Social';

import styles from './styles.module.scss';
import { SocialMediaCard } from 'components/CMSModals/socialMediaCard';

export type FeaturedResourcesItemProps = {
  resource: SocialMediaCard;
  primary?: boolean;
};

const FeaturedResourcesItem: React.FunctionComponent<FeaturedResourcesItemProps> =
  ({ primary, resource }) => {
    const isMobile = useMediaQuery({
      query: '(max-width: 896px) and (min-width: 768px), (max-width: 576px)',
    });

    const { accountDataContext } = useContext(AccountDataContext);

    const contentNew = useMemo(() => {
      if (accountDataContext) {
        return contentIsNew(
          accountDataContext?.lastLogin,
          resource.publicationDate.toString(),
          resource.rowKey,
          accountDataContext.userId,
        );
      }
    }, [accountDataContext, resource]);

    const classNames = clsx(
      styles.featuredResourcesItem,
      primary && styles.primary,
      contentNew && styles.isNewArticle,
    );
    return (
      <article className={classNames}>
        {primary &&
          (resource.imageUrl ? (
            <span
              style={{
                backgroundImage: `url(${resource.imageUrl}?format=jpg&auto=webp?quality=60)`,
              }}
            >
              <Hyperlink.PageLink
                onClick={() =>
                  localStorage.setItem('contentRowKey', resource.contentRowKey)
                }
                slug={getCMSLink(resource.navLink)}
              >
                <span></span>
              </Hyperlink.PageLink>
            </span>
          ) : (
            <Hyperlink.PageLink
              onClick={() =>
                localStorage.setItem('contentRowKey', resource.contentRowKey)
              }
              slug={getCMSLink(resource.navLink)}
            >
              <span className={styles.placeholderImage}></span>
            </Hyperlink.PageLink>
          ))}

        <div>
          <header>
            {accountDataContext && contentNew && (
              <span className={styles.newIcon}>NEW</span>
            )}
            <p className={styles.publication}>
              <span>
                {getDateFormatted(resource.publicationDate.toString())}
              </span>
              <span>{resource.readingTime}</span>
            </p>
            {!isMobile && (
              <Social
                bookmarked={resource.isSaved}
                className={styles.social}
                contentTypeUid={resource.title}
                liked={resource.isLiked}
                likes={resource.likeCount}
                pageSlug={resource.navLink}
              />
            )}
          </header>
          {(resource.title || resource.description) && (
            <div className={styles.copy}>
              {!!resource.title && (
                <h3>
                  <Hyperlink.PageLink
                    onClick={() =>
                      localStorage.setItem(
                        'contentRowKey',
                        resource.contentRowKey,
                      )
                    }
                    slug={getCMSLink(resource.navLink)}
                  >
                    {resource.title}
                  </Hyperlink.PageLink>
                </h3>
              )}
              {!!resource.description && !isMobile && (
                <p>
                  <Hyperlink.PageLink
                    onClick={() =>
                      localStorage.setItem(
                        'contentRowKey',
                        resource.contentRowKey,
                      )
                    }
                    slug={getCMSLink(resource.navLink)}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: resource.description,
                      }}
                    ></span>
                  </Hyperlink.PageLink>
                </p>
              )}
            </div>
          )}
          <footer>
            {isMobile && (
              <Social
                bookmarked={resource.isSaved}
                className={styles.social}
                contentTypeUid={resource.title}
                liked={resource.isLiked}
                likes={resource.likeCount}
                pageSlug={resource.navLink}
              />
            )}
            {(!primary || (primary && isMobile)) && (
              <Hyperlink.PageLink
                className={styles.hyperlink}
                label={linkText(resource.navText)}
                slug={getCMSLink(resource.navLink)}
                style={ButtonColour.BRAND_BERRY}
                onClick={() =>
                  localStorage.setItem('contentRowKey', resource.contentRowKey)
                }
              >
                <Chevron />
              </Hyperlink.PageLink>
            )}
            {primary && !isMobile && (
              <CtaButton
                defaultColour={ButtonColour.BRAND_BERRY}
                label={linkText(resource.navText)}
                page_link={getCMSLink(resource.navLink)}
                onClick={() =>
                  localStorage.setItem('contentRowKey', resource.contentRowKey)
                }
                small
              />
            )}
          </footer>
        </div>
      </article>
    );
  };

export default FeaturedResourcesItem;
